import React, { useEffect, useMemo, useState } from 'react';
import DatePicker from 'react-datepicker';
import { ChevronDownIcon, TrashIcon } from '@heroicons/react/24/outline';
import SearchableSelect from './SearchableSelect';

function ManualTransactionModal({
  show,
  modalRef,
  onClose,
  onSubmit,
  title = 'Add Transaction',
  manualFormError,
  manualFieldErrors,
  isMobileView,
  isManualDetailsOpen,
  setIsManualDetailsOpen,
  manualForm,
  quickManualItem,
  handleManualFormChange,
  handleQuickManualItemChange,
  handleManualItemCategorySelect,
  categorySelectOptions,
  quickCategoryAriaLabel,
  itemCategoryAriaLabelPrefix,
  manualTransactionDate,
  handleManualDateChange,
  manualRecurrenceStartDate,
  handleManualRecurrenceStartDateChange,
  manualRecurrenceEndDate,
  handleManualRecurrenceEndDateChange,
  handleManualRecurringToggle,
  recurrenceFrequencyOptions,
  manualRecurrencePreview,
  handleManualItemChange,
  removeManualItem,
  addManualItem,
  resolveManualItemTotal,
  manualItemsSubtotal,
  manualTaxAmount,
  manualGrandTotal,
  formatCurrency,
  creatingTransaction,
}) {
  const [collapsedItems, setCollapsedItems] = useState({});

  useEffect(() => {
    setCollapsedItems((current) => {
      const items = Array.isArray(manualForm.items) ? manualForm.items : [];
      const next = {};
      items.forEach((_, index) => {
        if (Object.prototype.hasOwnProperty.call(current, index)) {
          next[index] = Boolean(current[index]);
        } else {
          next[index] = index > 0;
        }
      });
      return next;
    });
  }, [manualForm.items]);

  const hasMultipleItems = (manualForm.items?.length || 0) > 1;
  const allCollapsed = useMemo(() => {
    if (!hasMultipleItems) {
      return false;
    }
    return manualForm.items.every((_, index) => Boolean(collapsedItems[index]));
  }, [collapsedItems, hasMultipleItems, manualForm.items]);

  const toggleItemCollapse = (index) => {
    setCollapsedItems((current) => ({
      ...current,
      [index]: !current[index],
    }));
  };

  const setAllItemsCollapsed = (collapsed) => {
    if (!hasMultipleItems) {
      return;
    }
    const next = {};
    manualForm.items.forEach((_, index) => {
      next[index] = collapsed;
    });
    setCollapsedItems(next);
  };

  const handleRemoveItem = (index) => {
    if (!hasMultipleItems || typeof removeManualItem !== 'function') {
      return;
    }
    const shouldRemove = window.confirm(`Remove Item ${index + 1}?`);
    if (!shouldRemove) {
      return;
    }
    removeManualItem(index);
  };

  if (!show) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/45 backdrop-blur-[2px] p-4 sm:items-center">
      <div
        ref={modalRef}
        className="mt-4 mb-20 w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl border border-gray-200 bg-white shadow-2xl sm:my-0"
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-4 sm:px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-2 py-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          >
            Close
          </button>
        </div>

        <form onSubmit={onSubmit} className="p-4 sm:p-5 space-y-4 sm:space-y-5">
          {manualFormError && (
            <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {manualFormError}
            </div>
          )}
          <p className="text-xs text-gray-500">Required fields are marked with *.</p>

          <div className="space-y-3">
            {isMobileView && !isManualDetailsOpen && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-3">
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <input
                      type="text"
                      value={manualForm.storeName}
                      onChange={(e) => handleManualFormChange('storeName', e.target.value)}
                      className="w-full rounded border border-gray-300 px-3 py-2 text-sm bg-white"
                      placeholder="Store Name *"
                      required
                    />
                    {manualFieldErrors.storeName && (
                      <p className="mt-1 text-xs text-red-600">{manualFieldErrors.storeName}</p>
                    )}
                  </div>

                  <div>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={quickManualItem.itemTotal}
                      onChange={(e) => handleQuickManualItemChange('itemTotal', e.target.value)}
                      className="w-full rounded border border-gray-300 px-3 py-2 text-sm bg-white"
                      placeholder="Amount *"
                      required
                    />
                    {manualFieldErrors.items?.[0]?.itemTotal && (
                      <p className="mt-1 text-xs text-red-600">{manualFieldErrors.items[0].itemTotal}</p>
                    )}
                  </div>

                  <div>
                    <SearchableSelect
                      value={String(quickManualItem.categoryId || '')}
                      onChange={(selectedValue) => handleManualItemCategorySelect(0, selectedValue)}
                      options={categorySelectOptions}
                      placeholder="Search category *"
                      ariaLabel={quickCategoryAriaLabel}
                      inputClassName="w-full rounded border border-gray-300 px-3 py-2 text-sm text-gray-900 disabled:bg-gray-100 focus:border-blue-400 focus:outline-none"
                      menuClassName="max-h-64"
                    />
                    {manualFieldErrors.items?.[0]?.categoryId && (
                      <p className="mt-1 text-xs text-red-600">{manualFieldErrors.items[0].categoryId}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between gap-2">
                  <p className="shrink-0 text-xs font-medium tracking-wide text-gray-500">Payment Type</p>
                  <div className="inline-flex w-[196px] shrink-0 overflow-hidden rounded-md border border-gray-300 bg-white">
                    <button
                      type="button"
                      onClick={() => handleManualFormChange('paymentMethod', 'Credit Card')}
                      className={`w-1/2 px-3 py-1.5 text-sm transition ${
                        manualForm.paymentMethod === 'Credit Card'
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      Credit Card
                    </button>
                    <button
                      type="button"
                      onClick={() => handleManualFormChange('paymentMethod', 'Cash')}
                      className={`w-1/2 border-l border-gray-300 px-3 py-1.5 text-sm transition ${
                        manualForm.paymentMethod === 'Cash'
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      Cash
                    </button>
                  </div>
                </div>
                {manualFieldErrors.paymentMethod && (
                  <p className="-mt-1 text-xs text-red-600">{manualFieldErrors.paymentMethod}</p>
                )}

                {manualFieldErrors.transactionDate && (
                  <p className="-mt-1 text-xs text-red-600">{manualFieldErrors.transactionDate}</p>
                )}

                <button
                  type="button"
                  onClick={() => setIsManualDetailsOpen(true)}
                  className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Add More Details
                </button>
              </div>
            )}

            {(!isMobileView || isManualDetailsOpen) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {isMobileView && (
                  <div className="md:col-span-2 flex items-center justify-between">
                    <p className="text-sm font-semibold text-gray-700">Additional Details</p>
                    <button
                      type="button"
                      onClick={() => setIsManualDetailsOpen(false)}
                      className="text-sm font-medium text-gray-600 transition hover:text-gray-800"
                    >
                      Show Less
                    </button>
                  </div>
                )}

                <div>
                  <DatePicker
                    selected={manualTransactionDate}
                    onChange={handleManualDateChange}
                    dateFormat="MM-dd-yyyy"
                    withPortal
                    portalId="root"
                    wrapperClassName="w-full"
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                    calendarClassName="rounded-lg border border-gray-200 shadow-lg"
                    placeholderText="Choose Transaction Date"
                    required
                  />
                  {manualFieldErrors.transactionDate && (
                    <p className="mt-1 text-xs text-red-600">{manualFieldErrors.transactionDate}</p>
                  )}
                </div>
                <div>
                  <input
                    type="text"
                    value={manualForm.storeName}
                    onChange={(e) => handleManualFormChange('storeName', e.target.value)}
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                    placeholder="Store Name *"
                    required
                  />
                  {manualFieldErrors.storeName && (
                    <p className="mt-1 text-xs text-red-600">{manualFieldErrors.storeName}</p>
                  )}
                </div>
                <div>
                  <input
                    type="text"
                    value={manualForm.storeLocation}
                    onChange={(e) => handleManualFormChange('storeLocation', e.target.value)}
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                    placeholder="Store Location"
                  />
                </div>

                {isMobileView ? (
                  <div className="md:col-span-2">
                    <div className="flex items-center justify-between gap-2 rounded border border-gray-300 bg-white px-3 py-2">
                      <p className="shrink-0 text-sm font-medium text-gray-600">Payment Type</p>
                      <div className="inline-flex w-[196px] shrink-0 overflow-hidden rounded-md border border-gray-300 bg-white">
                        <button
                          type="button"
                          onClick={() => handleManualFormChange('paymentMethod', 'Credit Card')}
                          className={`w-1/2 px-3 py-1.5 text-sm transition ${
                            manualForm.paymentMethod === 'Credit Card'
                              ? 'bg-blue-600 text-white'
                              : 'bg-white text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          Credit Card
                        </button>
                        <button
                          type="button"
                          onClick={() => handleManualFormChange('paymentMethod', 'Cash')}
                          className={`w-1/2 border-l border-gray-300 px-3 py-1.5 text-sm transition ${
                            manualForm.paymentMethod === 'Cash'
                              ? 'bg-blue-600 text-white'
                              : 'bg-white text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          Cash
                        </button>
                      </div>
                    </div>
                    {manualFieldErrors.paymentMethod && (
                      <p className="mt-1 text-xs text-red-600">{manualFieldErrors.paymentMethod}</p>
                    )}
                  </div>
                ) : (
                  <div>
                    <select
                      value={manualForm.paymentMethod}
                      onChange={(e) => handleManualFormChange('paymentMethod', e.target.value)}
                      className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                      required
                    >
                      <option value="" disabled>Payment Method</option>
                      <option value="Credit Card">Credit Card</option>
                      <option value="Cash">Cash</option>
                    </select>
                    {manualFieldErrors.paymentMethod && (
                      <p className="mt-1 text-xs text-red-600">{manualFieldErrors.paymentMethod}</p>
                    )}
                  </div>
                )}

                <div className="md:col-span-2">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={manualForm.taxAmount}
                    onChange={(e) => handleManualFormChange('taxAmount', e.target.value)}
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                    placeholder="Tax Amount (Optional)"
                  />
                  {manualFieldErrors.taxAmount && (
                    <p className="mt-1 text-xs text-red-600">{manualFieldErrors.taxAmount}</p>
                  )}
                </div>

                <div className="md:col-span-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-3">
                  <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
                    <input
                      type="checkbox"
                      checked={manualForm.isRecurring}
                      onChange={(event) => handleManualRecurringToggle(event.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    Recurring transaction
                  </label>

                  {manualForm.isRecurring && (
                    <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div>
                        <select
                          value={manualForm.recurrenceFrequency}
                          onChange={(e) => handleManualFormChange('recurrenceFrequency', e.target.value)}
                          className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                          required={manualForm.isRecurring}
                        >
                          <option value="" disabled>
                            Frequency
                          </option>
                          {recurrenceFrequencyOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <DatePicker
                          selected={manualRecurrenceStartDate}
                          onChange={handleManualRecurrenceStartDateChange}
                          dateFormat="MM-dd-yyyy"
                          withPortal
                          portalId="root"
                          wrapperClassName="w-full"
                          className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                          placeholderText="Start Date"
                          required={manualForm.isRecurring}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <DatePicker
                          selected={manualRecurrenceEndDate}
                          onChange={handleManualRecurrenceEndDateChange}
                          minDate={manualRecurrenceStartDate || undefined}
                          dateFormat="MM-dd-yyyy"
                          withPortal
                          portalId="root"
                          wrapperClassName="w-full"
                          className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                          placeholderText="End Date (Optional)"
                          isClearable
                        />
                      </div>
                      {manualRecurrencePreview && (
                        <p className="md:col-span-2 text-xs text-gray-600">{manualRecurrencePreview}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {(!isMobileView || isManualDetailsOpen) && (
            <div>
              <div className="mb-2 flex items-center justify-between gap-2">
                <h3 className="text-sm font-semibold text-gray-900">Items</h3>
                {hasMultipleItems && (
                  <button
                    type="button"
                    onClick={() => setAllItemsCollapsed(!allCollapsed)}
                    className="text-xs font-medium text-blue-700 hover:text-blue-800"
                  >
                    {allCollapsed ? 'Expand All' : 'Collapse All'}
                  </button>
                )}
              </div>
              <div className="space-y-3">
                {manualForm.items.map((item, index) => (
                  <div key={`manual-item-${index}`}>
                    <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-3 shadow-sm">
                      <button
                        type="button"
                        onClick={() => toggleItemCollapse(index)}
                        className="mb-3 flex w-full items-center justify-between rounded-lg bg-slate-100 px-2.5 py-1.5 text-left text-xs font-semibold text-slate-700 hover:bg-slate-200"
                      >
                        <span>Item {index + 1}</span>
                        <span className="inline-flex items-center gap-2">
                          {hasMultipleItems && (
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleRemoveItem(index);
                              }}
                              className="rounded-md p-1 text-red-600 hover:bg-red-50 hover:text-red-700"
                              aria-label={`Remove item ${index + 1}`}
                              title="Remove item"
                            >
                              <TrashIcon className="h-4 w-4" aria-hidden="true" />
                            </button>
                          )}
                          <ChevronDownIcon
                            className={`h-4 w-4 transition-transform ${collapsedItems[index] ? '' : 'rotate-180'}`}
                            aria-hidden="true"
                          />
                        </span>
                      </button>
                      {!collapsedItems[index] && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <p className="mb-1 text-xs font-medium text-gray-600">Item Name</p>
                          <input
                            type="text"
                            value={item.itemName}
                            onChange={(e) => handleManualItemChange(index, 'itemName', e.target.value)}
                            className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                            placeholder="Item Name *"
                            required
                          />
                          {manualFieldErrors.items?.[index]?.itemName && (
                            <p className="mt-1 text-xs text-red-600">{manualFieldErrors.items[index].itemName}</p>
                          )}
                        </div>
                        <div>
                          <p className="mb-1 text-xs font-medium text-gray-600">Category *</p>
                          <SearchableSelect
                            value={String(item.categoryId || '')}
                            onChange={(selectedValue) => {
                              handleManualItemCategorySelect(index, selectedValue);
                            }}
                            options={categorySelectOptions}
                            placeholder="Search category *"
                            ariaLabel={`${itemCategoryAriaLabelPrefix} ${index + 1}`}
                            inputClassName="w-full rounded border border-gray-300 px-3 py-2 text-sm text-gray-900 disabled:bg-gray-100 focus:border-blue-400 focus:outline-none"
                            menuClassName="max-h-64"
                          />
                          {manualFieldErrors.items?.[index]?.categoryId && (
                            <p className="mt-1 text-xs text-red-600">{manualFieldErrors.items[index].categoryId}</p>
                          )}
                        </div>
                        <div>
                          <p className="mb-1 text-xs font-medium text-gray-600">Unit Price</p>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={item.unitPrice}
                            onChange={(e) => handleManualItemChange(index, 'unitPrice', e.target.value)}
                            className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                            placeholder="Unit Price"
                          />
                          {manualFieldErrors.items?.[index]?.unitPrice && (
                            <p className="mt-1 text-xs text-red-600">{manualFieldErrors.items[index].unitPrice}</p>
                          )}
                        </div>
                        <div>
                          <p className="mb-1 text-xs font-medium text-gray-600">Quantity</p>
                          <input
                            type="number"
                            step="1"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => handleManualItemChange(index, 'quantity', e.target.value)}
                            className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                            placeholder="Quantity"
                          />
                          {manualFieldErrors.items?.[index]?.quantity && (
                            <p className="mt-1 text-xs text-red-600">{manualFieldErrors.items[index].quantity}</p>
                          )}
                        </div>

                        <div className="md:col-span-2">
                          <p className="mb-1 text-xs font-medium text-gray-600">Item Total</p>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={
                              !item.itemTotalOverridden && resolveManualItemTotal(item) !== null
                                ? String(resolveManualItemTotal(item))
                                : item.itemTotal
                            }
                            onChange={(e) => handleManualItemChange(index, 'itemTotal', e.target.value)}
                            className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                            placeholder="Item Total"
                          />
                          {!item.itemTotalOverridden && resolveManualItemTotal(item) !== null && (
                            <p className="mt-1 text-xs text-slate-500">
                              Auto-calculated from quantity x unit price. You can override this value.
                            </p>
                          )}
                          {manualFieldErrors.items?.[index]?.itemTotal && (
                            <p className="mt-1 text-xs text-red-600">{manualFieldErrors.items[index].itemTotal}</p>
                          )}
                        </div>
                      </div>
                      )}
                    </div>
                    {index === manualForm.items.length - 1 && (
                      <div className="mt-2 flex justify-end">
                        <button
                          type="button"
                          onClick={addManualItem}
                          className="rounded border border-blue-300 px-3 py-1.5 text-sm text-blue-700 hover:bg-blue-50"
                        >
                          + Add Item
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-3 rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-semibold text-gray-900">{formatCurrency(manualItemsSubtotal)}</span>
                </div>
                <div className="mt-1 flex justify-between">
                  <span className="text-gray-600">Tax</span>
                  <span className="font-semibold text-gray-900">{formatCurrency(manualTaxAmount)}</span>
                </div>
                <div className="mt-1 flex justify-between border-t border-gray-200 pt-1">
                  <span className="text-gray-900 font-medium">Total</span>
                  <span className="font-semibold text-gray-900">{formatCurrency(manualGrandTotal)}</span>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={creatingTransaction}
              className="rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:bg-blue-400"
            >
              {creatingTransaction
                ? 'Saving...'
                : manualForm.items.length > 1
                  ? 'Save Transactions'
                  : 'Save Transaction'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ManualTransactionModal;
